// Add-block and shift-blocks modals for the day-plans grid.
// ADD_BLOCK makes one independent copy of the block per day in `dayIndexes`. SHIFT_BLOCKS has two
// 400 guards: a zero shift, and a result with a dayIndex below 1.
import { useEffect, useState } from "react";
import { Alert, Form, InputNumber, Modal, Tag, Typography } from "antd";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { sameBlockContent } from "@utils/rtp_utils";
import { rtpApi, runRtpWrite } from "./rtpHooks";
import type { RtpBlockLibraryState } from "./rtpHooks";
import { BLOCK_CONTENT_FIELDS, BlockContentFields, buildBlockContent, hasBlockContent } from "./BlockForm";
import type { BlockFormValues } from "./BlockForm";
import { BlockLibraryPicker } from "./BlockLibraryPicker";
import { BlockLibraryStar } from "./BlockLibraryStar";
import type { BlockStarPlan } from "./BlockLibraryStar";
import type { WeekCell } from "./DayPlansTab";
import type { RtpPhase } from "./types";

const { Text } = Typography;

interface AddBlockModalProps {
	phase: RtpPhase;
	weekCells: WeekCell[];
	// Enables the player-stats hint under the target rows.
	player?: { id: number; name: string };
	library: RtpBlockLibraryState;
	open: boolean;
	onClose: () => void;
	refetch: () => void;
}

interface AddBlockFormValues extends BlockFormValues {
	days: number[];
}

interface DayPillsProps {
	cells: WeekCell[];
	// Injected by Form.Item.
	value?: number[];
	onChange?: (value: number[]) => void;
}

// Pill picker over the week currently shown in the grid; out-of-phase days render disabled.
const DayPills = ({ cells, value = [], onChange }: DayPillsProps) => (
	<div className="rtp-pills rtp-pills--fill">
		{cells.map((cell) => {
			const checked = value.includes(cell.dayIndex);
			return (
				<Tag.CheckableTag
					key={cell.dayIndex}
					checked={checked}
					className={cell.inPhase ? undefined : "rtp-pill-disabled"}
					onChange={() => {
						if (!cell.inPhase) return;
						onChange?.(
							checked
								? value.filter((d) => d !== cell.dayIndex)
								: [...value, cell.dayIndex].sort((a, b) => a - b)
						);
					}}
				>
					{cell.date ? cell.date.format("ddd D") : `Day ${cell.dayIndex}`}
				</Tag.CheckableTag>
			);
		})}
	</div>
);

export const AddBlockModal = ({ phase, weekCells, player, library, open, onClose, refetch }: AddBlockModalProps) => {
	const [form] = Form.useForm<AddBlockFormValues>();
	const [submitting, setSubmitting] = useState(false);
	const [contentError, setContentError] = useState(false);
	// The library entry the user picked or saved with the star. ADD_BLOCK sends it as `templateId`.
	const [templateId, setTemplateId] = useState<string | null>(null);
	const watched = Form.useWatch([], form);
	// A deleted entry is not in the list: ADD_BLOCK gets a 400 for its id, so the dialog drops it.
	const entry = library.templates.find((item) => item.id === templateId);
	// The star is filled only while the form still holds the content of the entry. An edited copy
	// keeps `templateId` for ADD_BLOCK, and a click on the empty star saves it as a new entry.
	const starEntryId = entry && sameBlockContent(buildBlockContent(watched ?? {}), entry.content) ? entry.id : null;

	useEffect(() => {
		if (!open) return;
		form.resetFields();
		setContentError(false);
		setTemplateId(null);
		// Reset only when the modal opens — not on every plan refetch while it's already open,
		// which would clobber in-progress edits.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const handleClose = () => {
		onClose();
		form.resetFields();
	};

	const handleSubmit = async () => {
		// Checked before validateFields, so a missing day and missing content both show at once.
		const noContent = !hasBlockContent(buildBlockContent(form.getFieldsValue()));
		setContentError(noContent);
		let values: AddBlockFormValues;
		try {
			values = await form.validateFields();
		} catch {
			return;
		}
		if (noContent) return;
		setSubmitting(true);
		const content = buildBlockContent(values);
		const ok = await runRtpWrite(
			() =>
				rtpApi.rtpPhaseAction(phase.id, {
					action: "ADD_BLOCK",
					dayIndexes: values.days,
					content,
					templateId: entry?.id,
				}),
			() => {
				refetch();
				// The block is a new use of the entry, so the usage counts change.
				if (entry) library.reload();
			}
		);
		setSubmitting(false);
		if (ok) handleClose();
	};

	// The star needs content and valid rows, but not the days.
	const prepareStar = async (): Promise<BlockStarPlan> => {
		const noContent = !hasBlockContent(buildBlockContent(form.getFieldsValue()));
		setContentError(noContent);
		if (noContent) return null;
		try {
			await form.validateFields(BLOCK_CONTENT_FIELDS, { recursive: true });
		} catch {
			return null;
		}
		return { content: buildBlockContent(form.getFieldsValue()) };
	};

	return (
		<Modal
			className="rtp-wz rtp-crit-modal"
			title={
				<div className="rtp-lib-title">
					Add Block
					<BlockLibraryPicker templates={library.templates} form={form} onPick={setTemplateId} />
					<BlockLibraryStar
						library={library}
						templateId={starEntryId}
						readOnly={!library.canCreate}
						prepare={prepareStar}
						onLinkChange={setTemplateId}
					/>
				</div>
			}
			open={open}
			onCancel={handleClose}
			onOk={handleSubmit}
			confirmLoading={submitting}
			destroyOnHidden
			okButtonProps={{ className: "rtp-wz-primary-btn", "data-attr": "settings.rtp-block.add-submit" } as any}
		>
			<Form form={form} layout="vertical">
				<BlockContentFields player={player} contentError={contentError} />

				<Form.Item
					name="days"
					label="Days"
					rules={[{ required: true, type: "array", message: "Pick at least one day" }]}
					extra="One independent copy is created per day — later edits to one day never touch another."
				>
					<DayPills cells={weekCells} />
				</Form.Item>
			</Form>
		</Modal>
	);
};

interface ShiftBlocksModalProps {
	phase: RtpPhase;
	open: boolean;
	onClose: () => void;
	refetch: () => void;
}

interface ShiftFormValues {
	fromDay: number;
	shiftBy: number;
}

export const ShiftBlocksModal = ({ phase, open, onClose, refetch }: ShiftBlocksModalProps) => {
	const [form] = Form.useForm<ShiftFormValues>();
	const [submitting, setSubmitting] = useState(false);
	const fromDay = Form.useWatch("fromDay", form);
	const shiftBy = Form.useWatch("shiftBy", form);

	useEffect(() => {
		if (!open) return;
		form.resetFields();
		form.setFieldsValue({ fromDay: 1 });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const handleClose = () => {
		onClose();
		form.resetFields();
	};

	// Mirrors the backend's SHIFT_BLOCKS guards: a zero/empty shift, or a shift that would
	// carry the earliest affected block below day 1, both 400 server-side.
	const effectiveFromDay = fromDay ?? 1;
	const affectedBlocks = phase.blocks.filter((b) => b.dayIndex >= effectiveFromDay);
	const minAffectedDay = affectedBlocks.length > 0 ? Math.min(...affectedBlocks.map((b) => b.dayIndex)) : null;
	const shiftMissing = shiftBy == null || shiftBy === 0;
	const wouldGoBelowOne = !shiftMissing && minAffectedDay != null && minAffectedDay + shiftBy < 1;
	const submitDisabled = shiftMissing || wouldGoBelowOne;

	const handleSubmit = async () => {
		let values: ShiftFormValues;
		try {
			values = await form.validateFields();
		} catch {
			return;
		}
		setSubmitting(true);
		const ok = await runRtpWrite(
			() => rtpApi.rtpPhaseAction(phase.id, { action: "SHIFT_BLOCKS", days: values.shiftBy, fromDay: values.fromDay }),
			refetch
		);
		setSubmitting(false);
		if (ok) {
			message.success(
				`Shifted ${affectedBlocks.length} block${affectedBlocks.length === 1 ? "" : "s"} by ${values.shiftBy} day${
					Math.abs(values.shiftBy) === 1 ? "" : "s"
				}.`
			);
			handleClose();
		}
	};

	return (
		<Modal
			title="Shift Blocks"
			open={open}
			onCancel={handleClose}
			onOk={handleSubmit}
			confirmLoading={submitting}
			destroyOnHidden
			okButtonProps={{ disabled: submitDisabled, "data-attr": "settings.rtp-block.shift-submit" } as any}
		>
			<Form form={form} layout="vertical">
				<Form.Item name="fromDay" label="From day" rules={[{ required: true, message: "Enter a starting day" }]}>
					<InputNumber min={1} precision={0} style={{ width: "100%" }} />
				</Form.Item>
				<Form.Item name="shiftBy" label="Shift by (days)" rules={[{ required: true, message: "Enter a shift amount" }]}>
					<InputNumber precision={0} style={{ width: "100%" }} placeholder="e.g. 2 or -2" />
				</Form.Item>

				<Text type="secondary">
					{affectedBlocks.length} block{affectedBlocks.length === 1 ? "" : "s"} on day {effectiveFromDay} or later
					will move.
				</Text>

				{shiftMissing ? (
					<Alert style={{ marginTop: 12 }} type="info" showIcon message="Enter a non-zero shift to continue." />
				) : null}
				{wouldGoBelowOne ? (
					<Alert
						style={{ marginTop: 12 }}
						type="error"
						showIcon
						message={`This would move day ${minAffectedDay} to day ${minAffectedDay + shiftBy} — days can't go below 1.`}
					/>
				) : null}
			</Form>
		</Modal>
	);
};
